<script setup lang="ts">
import { reactive, ref } from 'vue';
import { CheckCircle2Icon } from 'lucide-vue-next';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import Input from '@/components/ui/input/Input.vue';
import Label from '@/components/ui/label/Label.vue';
import http from '@/lib/http';
import Button from '../ui/button/Button.vue';
import Spinner from '../ui/spinner/Spinner.vue';

const props = withDefaults(
  defineProps<{
    offerId: number;
    form_type_id?: number;
  }>(),
  {
    form_type_id: 4,
  },
);

const open = defineModel<boolean>({ required: true });

const form = reactive({ name: '', phone: '' });
const loading = ref(false);
const error = ref('');
const submitted = ref(false);

const reset = () => {
  form.name = '';
  form.phone = '';
  error.value = '';
  submitted.value = false;
};

const PHONE_LENGTH = 18;

const submit = async () => {
  error.value = '';

  if (form.phone.length !== PHONE_LENGTH) {
    error.value = 'Введите номер телефона полностью';
    return;
  }

  loading.value = true;

  await http
    .post('/form/send', {
      form_type_id: props.form_type_id,
      offer_id: props.offerId,
      name: form.name,
      phone: form.phone,
    })
    .then(() => {
      submitted.value = true;
      setTimeout(() => {
        open.value = false;
        reset();
      }, 2800);
    })
    .catch(() => {
      error.value = 'Произошла ошибка. Пожалуйста, попробуйте ещё раз.';
    })
    .finally(() => {
      loading.value = false;
    });
};
</script>

<template>
  <Dialog
    v-model:open="open"
    @update:open="
      (val) => {
        if (!val) reset();
      }
    "
  >
    <DialogContent
      class="gap-0 overflow-hidden rounded-sm border-0 p-0 shadow-2xl *:data-[slot=dialog-close]:hover:text-white sm:max-w-md"
    >
      <div class="relative bg-primary px-7 pt-7 pb-6">
        <DialogHeader class="space-y-0">
          <DialogTitle class="text-lg leading-snug font-semibold tracking-tight text-white">
            Получить предложение
          </DialogTitle>
          <p class="mt-1.5 text-sm font-normal text-secondary">Уточним наличие и подберём лучшую цену для вас</p>
        </DialogHeader>
      </div>

      <Transition
        enter-active-class="transition-all duration-500 ease-out"
        enter-from-class="opacity-0 scale-95"
        enter-to-class="opacity-100 scale-100"
      >
        <div v-if="submitted" class="flex flex-col items-center gap-4 bg-white px-7 py-12">
          <div class="relative">
            <div class="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50">
              <CheckCircle2Icon class="h-8 w-8 text-emerald-500" :stroke-width="1.5" />
            </div>
            <div class="absolute inset-0 animate-ping rounded-full bg-emerald-400/20" />
          </div>
          <div class="text-center">
            <p class="text-base font-semibold text-zinc-900">Запрос отправлен</p>
            <p class="mt-1 text-sm text-zinc-500">Наш специалист свяжется с вами в ближайшее время</p>
          </div>
        </div>
      </Transition>

      <form v-if="!submitted" @submit.prevent="submit" class="space-y-5 bg-white px-7 py-6">
        <div class="space-y-1.5">
          <Label for="offer-name" class="text-[11px] font-bold tracking-widest text-muted-foreground uppercase">
            ФИО
          </Label>
          <Input
            id="offer-name"
            v-model="form.name"
            placeholder="Иванов Иван Иванович"
            autocomplete="name"
            required
            class="h-11"
          />
        </div>

        <div class="space-y-1.5">
          <Label for="offer-phone" class="text-[11px] font-bold tracking-widest text-muted-foreground uppercase">
            Телефон
          </Label>
          <Input
            id="offer-phone"
            v-model="form.phone"
            type="tel"
            placeholder="+7 (___) ___ __-__"
            autocomplete="tel"
            v-maska="'+7 (###) ### ##-##'"
            required
            class="h-11"
          />
        </div>

        <div v-if="error" class="rounded-sm border border-red-100 bg-red-50 px-3 py-2.5 text-sm text-red-600">
          {{ error }}
        </div>

        <Button
          type="submit"
          :disabled="loading"
          class="flex h-12 w-full cursor-pointer items-center justify-center gap-2.5 text-sm font-bold tracking-[0.12em] uppercase duration-150"
        >
          <Spinner v-if="loading" class="size-4" />
          {{ loading ? 'Отправка...' : 'Получить предложение' }}
        </Button>

        <p class="text-xs leading-relaxed text-zinc-400">
          Нажимая «Получить предложение», вы соглашаетесь с политикой обработки персональных данных и конфиденциальности
        </p>
      </form>
    </DialogContent>
  </Dialog>
</template>
